import type { DefaultValues, SubmitHandler } from 'react-hook-form';
import { useState } from 'react';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Snackbar } from '@sixui/ui';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import type { IContentBlockFormBodyProps } from '~/components/molecules/ContentBlock';
import type { IFileUploaderValue } from '~/components/organisms/fields/FileUploader';
import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { appQueryKeyFactory } from '~/lib/api/modules/Apps/hooks/useAppQuery';
import { useUpdateAppMutation } from '~/lib/api/modules/Apps/hooks/useUpdateAppMutation';
import { StartupScreenSettingsFormMainSection } from './StartupScreenSettingsFormMainSection';

export type IStartupScreenSettingsFormValues = {
  videos?: Array<IFileUploaderValue>;
  mode?: 'always' | 'once' | null;
};

const SNACKBAR_AUTO_HIDE_DURATION = 3000;

export type IStartupScreenSettingsFormProps = IContainerProps &
  Omit<
    IContentBlockFormBodyProps<IStartupScreenSettingsFormValues>,
    'form' | 'children' | 'onSubmit' | 'submitting'
  > & {
    appId: string;
    defaultValues?: DefaultValues<IStartupScreenSettingsFormValues>;
    onSubmit?: SubmitHandler<IStartupScreenSettingsFormValues>;
  };

export const StartupScreenSettingsForm: React.FC<
  IStartupScreenSettingsFormProps
> = (props) => {
  const { sx, appId, defaultValues, onSubmit, ...other } = props;
  const { t } = useTranslation();
  const updateAppMutation = useUpdateAppMutation();
  const [submitted, setSubmitted] = useState(false);
  const queryClient = useQueryClient();

  const form = useForm<IStartupScreenSettingsFormValues>({
    defaultValues,
  });

  const handleDelete = (): Promise<unknown> | undefined => {
    if (updateAppMutation.isLoading) {
      return;
    }

    return updateAppMutation.mutateAsync(
      {
        appId,
        body: {
          startupVideo: {
            delete: true,
          },
        },
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          void queryClient.invalidateQueries({
            queryKey: appQueryKeyFactory({
              appId,
            }),
          });
          form.setValue('videos', []);
          form.setValue('mode', null);
        },
      },
    );
  };

  return (
    <>
      <Snackbar
        open={submitted}
        onClose={() => setSubmitted(false)}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
      >
        {t('common.successfullySaved')}
      </Snackbar>

      <ContentBlock.FormBody
        sx={sx}
        form={form}
        onSubmit={onSubmit}
        extraActions={
          <Button
            variant="danger"
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={handleDelete}
          >
            {t('common.delete')}
          </Button>
        }
        {...other}
      >
        <StartupScreenSettingsFormMainSection form={form} appId={appId} />
      </ContentBlock.FormBody>
    </>
  );
};
